import { useCallback, useState } from "react";
import { validateFieldValue, validateFormValues } from "utils/validators";

const DEFAULT_VALIDATION_OPTIONS = {};

const useFormValidation = (
  validationRules = {},
  options = DEFAULT_VALIDATION_OPTIONS,
) => {
  const [errors, setErrors] = useState({});

  const validateForm = useCallback(
    (formData) => {
      const validationErrors = validateFormValues(
        formData,
        validationRules,
        options,
      );
      setErrors(validationErrors);
      return Object.keys(validationErrors).length === 0;
    },
    [validationRules, options],
  );

  const validateField = useCallback(
    (fieldName, value, formData = {}) => {
      const error = validateFieldValue(
        fieldName,
        value,
        validationRules[fieldName],
        { ...formData, [fieldName]: value },
        options,
      );

      setErrors((prev) => {
        const nextErrors = { ...prev };

        if (error) {
          nextErrors[fieldName] = error;
        } else {
          delete nextErrors[fieldName];
        }

        return nextErrors;
      });

      return error;
    },
    [validationRules, options],
  );

  const clearFieldError = useCallback((fieldName) => {
    setErrors((prev) => {
      if (!prev[fieldName]) return prev;

      const nextErrors = { ...prev };
      delete nextErrors[fieldName];
      return nextErrors;
    });
  }, []);

  const resetErrors = useCallback(() => {
    setErrors({});
  }, []);

  return {
    errors,
    setErrors,
    validateForm,
    validateField,
    clearFieldError,
    resetErrors,
    hasErrors: Object.keys(errors).length > 0,
  };
};

export default useFormValidation;
